import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { chartUrl } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class SocketConnectionService {

  private socket: Socket | null = null;

  private connectionStatusSubject = new BehaviorSubject<boolean>(false);
  public connectionStatus$: Observable<boolean> =
    this.connectionStatusSubject.asObservable();

  private errorSubject = new BehaviorSubject<string | null>(null);
  public errors$ = this.errorSubject.asObservable();

  constructor() {
    console.log('[SocketConnection] Initialized');
  }

  // =============================
  // CONNECT
  // =============================
  connect(userId: string, namespace = '/chart'): void {
    if (this.socket?.connected) {
      console.warn('[SocketConnection] Already connected');
      return;
    }

    console.log('[SocketConnection] Connecting →', `${chartUrl}${namespace}`);

    this.socket = io(`${chartUrl}${namespace}`, {
      transports: ['websocket'],
      query: { user: userId },
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 3000
    });

    this.registerCoreListeners();
  }

  // =============================
  // CORE LISTENERS
  // =============================
  private registerCoreListeners(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      console.log('[SocketConnection] ✅ Connected:', this.socket?.id);
      this.connectionStatusSubject.next(true);
    });

    this.socket.on('disconnect', (reason) => {
      console.warn('[SocketConnection] ❌ Disconnected:', reason);
      this.connectionStatusSubject.next(false);
    });

    this.socket.on('connect_error', (err) => {
      console.error('[SocketConnection] ❌ Connect error:', err.message);
      this.errorSubject.next(err.message);
    });
  }

  // =============================
  // EMIT (RAW)
  // =============================
  emit(event: string, payload?: any): void {
    if (!this.isConnected()) {
      console.warn('[SocketConnection] Emit failed – not connected');
      return;
    }

    console.log(`[SocketConnection] 📤 ${event}`, payload);
    this.socket!.emit(event, payload);
  }

  // =============================
  // LISTEN (RAW)
  // =============================
  on<T = any>(event: string, callback: (data: T) => void): void {
    if (!this.socket) return;

    console.log(`[SocketConnection] 👂 Listening → ${event}`);
    this.socket.on(event, callback);
  }

  off(event: string): void {
    this.socket?.off(event);
  }

  // =============================
  // STATE
  // =============================
  isConnected(): boolean {
    return !!this.socket?.connected;
  }

  // =============================
  // DISCONNECT
  // =============================
  disconnect(): void {
    if (!this.socket) return;

    console.log('[SocketConnection] Disconnecting...');
    this.socket.removeAllListeners();
    this.socket.disconnect();
    this.socket = null;
    this.connectionStatusSubject.next(false);
  }
}
